import { X } from 'lucide-react'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { RichTextEditor } from '@/components/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { deleteTask } from '@/features/preinvest/tasks/api'
import { isRichTextEmpty, trimRichText } from '@/lib/richText'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { todayIso } from '../dates'
import { addDays } from '../record/format'
import { createActivity, createRelationTask } from './api'
import { clearDraft, composerDraftKey, readDraft, writeDraft } from './drafts'
import type { ActivityInput, RelationRef, TimelineActivity } from './types'

export type ComposerType = 'note' | 'call' | 'meeting' | 'email'

export interface ComposerRelation extends RelationRef {
  name: string
  /** Contact page: the primary organisation, which comes along unless removed. */
  primaryOrganisation?: { id: string; name: string } | null
  /** Organisation page: the current contact persons, one of whom may be chosen. */
  members?: { person_id: string; full_name: string }[]
}

interface ComposerDraft {
  occurred_at: string
  title: string
  body: string
  withOrganisation: boolean
  personId: string | null
}

/** What a call or meeting leaves to do: the task that "Vervolgtaak?" offers. */
interface FollowUp {
  title: string
  person_id: string | null
  organisation_id: string | null
}

const TYPES: { key: ComposerType; label: string }[] = [
  { key: 'note', label: 'Notitie' },
  { key: 'call', label: 'Gesprek' },
  { key: 'meeting', label: 'Meeting' },
  { key: 'email', label: 'E-mail' },
]

const FOLLOW_UPS = [
  { label: 'Morgen', days: 1 },
  { label: 'Volgende week', days: 7 },
  { label: 'Over een maand', days: 30 },
] as const

const selectClass =
  'h-8 rounded-md border border-border bg-background px-2 text-sm max-md:h-11 max-md:text-base'

function emptyDraft(): ComposerDraft {
  return { occurred_at: todayIso(), title: '', body: '', withOrganisation: true, personId: null }
}

function hasContent(draft: ComposerDraft): boolean {
  return draft.title.trim() !== '' || !isRichTextEmpty(draft.body)
}

function isComposerDraft(value: unknown): value is ComposerDraft {
  if (typeof value !== 'object' || value === null) return false
  const draft = value as Record<string, unknown>
  return (
    typeof draft.occurred_at === 'string' &&
    typeof draft.title === 'string' &&
    typeof draft.body === 'string' &&
    typeof draft.withOrganisation === 'boolean' &&
    (draft.personId === null || typeof draft.personId === 'string')
  )
}

/**
 * The composer at the top of the middle panel (ED-19): Notitie, Gesprek, Meeting and E-mail
 * (gelogd). Collapsed it is one line; open it holds the type, date and title (not for a
 * note), who it was with, the rich text and the buttons. The draft lives in `sessionStorage`
 * per relation and type: Escape folds the composer and keeps it, "Annuleren" throws it away
 * on purpose, a failed save keeps it, ⌘/Ctrl+Enter saves (IA-15, ED-52). After a call or
 * meeting one click on a quick pick plans the follow-up task.
 */
export function ActivityComposer({
  relation,
  open,
  type,
  autoFocus = false,
  onAutoFocused,
  onOpenChange,
  onTypeChange,
  onSaved,
  onTaskCreated,
}: {
  relation: ComposerRelation
  open: boolean
  type: ComposerType
  /** A quick action asks for the editor: scroll the composer into view and focus the editor. */
  autoFocus?: boolean
  /**
   * Called once the request in `autoFocus` has been honoured; the page lowers `autoFocus` then.
   * A request is good for one focus: a change of tab mounts the composer again in the other
   * panel, and a request still up would take the focus from the tab.
   */
  onAutoFocused?: () => void
  onOpenChange: (open: boolean) => void
  onTypeChange: (type: ComposerType) => void
  onSaved: (item: TimelineActivity) => void
  onTaskCreated: () => void
}) {
  const key = composerDraftKey(relation, type)
  const [draft, setDraft] = useState<ComposerDraft>(() => readDraft(key, isComposerDraft) ?? emptyDraft())
  const [draftKey, setDraftKey] = useState(key)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [followUp, setFollowUp] = useState<FollowUp | null>(null)
  const [pickingDate, setPickingDate] = useState(false)
  const [followUpDate, setFollowUpDate] = useState('')
  const [followUpBusy, setFollowUpBusy] = useState(false)
  const section = useRef<HTMLElement>(null)
  const openButton = useRef<HTMLButtonElement>(null)
  const radios = useRef(new Map<ComposerType, HTMLButtonElement>())
  // Set when the composer folds itself, so the focus goes back to its collapsed line.
  const focusLine = useRef(false)

  // A draft per relation and type: another key loads that key's draft ("adjusting state when
  // a prop changes", like E1's ConsentRow).
  if (draftKey !== key) {
    setDraftKey(key)
    setDraft(readDraft(key, isComposerDraft) ?? emptyDraft())
    setError(null)
  }

  // The storage follows the draft: text writes it, none clears it (a save clears it itself).
  useEffect(() => {
    if (hasContent(draft)) writeDraft(key, draft)
    else clearDraft(key)
  }, [draft, key])

  useEffect(() => {
    if (!open || !autoFocus) return
    const node = section.current
    node?.scrollIntoView({ block: 'nearest' })
    node?.querySelector<HTMLElement>('[role="textbox"], textarea')?.focus()
    onAutoFocused?.()
  }, [open, autoFocus, onAutoFocused])

  useEffect(() => {
    if (open || !focusLine.current) return
    focusLine.current = false
    openButton.current?.focus()
  }, [open])

  const members = relation.members ?? []
  const personId =
    relation.kind === 'organisation' && members.some((member) => member.person_id === draft.personId)
      ? draft.personId
      : null
  const primary = relation.kind === 'contact' ? (relation.primaryOrganisation ?? null) : null
  const canSave = !saving && (type !== 'note' || !isRichTextEmpty(draft.body))

  function update(patch: Partial<ComposerDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
  }

  function fold() {
    focusLine.current = true
    onOpenChange(false)
  }

  function cancel() {
    clearDraft(key)
    setDraft(emptyDraft())
    setError(null)
    fold()
  }

  async function save() {
    if (!canSave) return
    setSaving(true)
    setError(null)
    const title = type === 'note' ? null : draft.title.trim() || null
    const input: ActivityInput = {
      type,
      occurred_at: type === 'note' ? undefined : draft.occurred_at || undefined,
      title,
      body_html: isRichTextEmpty(draft.body) ? null : trimRichText(draft.body),
      person_id: relation.kind === 'contact' ? relation.id : personId,
      organisation_id:
        relation.kind === 'organisation' ? relation.id : draft.withOrganisation ? (primary?.id ?? null) : null,
    }
    try {
      const item = await createActivity(input)
      clearDraft(key)
      setDraft(emptyDraft())
      fold()
      onSaved(item)
      for (const rhythm of item.rhythms_met ?? []) toast.success(`Contactritme '${rhythm}' gehaald`)
      if (type === 'call' || type === 'meeting') {
        setFollowUp({
          title: `Opvolgen: ${title ?? relation.name}`,
          person_id: input.person_id ?? null,
          organisation_id: input.organisation_id ?? null,
        })
        setPickingDate(false)
        setFollowUpDate(addDays(todayIso(), 7))
      } else {
        setFollowUp(null)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Opslaan mislukt.')
    } finally {
      setSaving(false)
    }
  }

  async function planFollowUp(dueDate: string) {
    if (!followUp || followUpBusy) return
    setFollowUpBusy(true)
    try {
      const task = await createRelationTask({
        title: followUp.title,
        due_date: dueDate,
        person_id: followUp.person_id,
        organisation_id: followUp.organisation_id,
      })
      setFollowUp(null)
      setPickingDate(false)
      onTaskCreated()
      toastUndo('Vervolgtaak aangemaakt.', async () => {
        await deleteTask(task.id)
        onTaskCreated()
      })
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Vervolgtaak aanmaken mislukt.')
    } finally {
      setFollowUpBusy(false)
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (!open) return
    if (event.key === 'Escape') {
      event.preventDefault()
      fold()
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      void save()
    }
  }

  // ARIA radio group with a roving tab stop: the arrows move and choose.
  function onRadioKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
    if (step === 0) return
    event.preventDefault()
    const next = TYPES[(index + step + TYPES.length) % TYPES.length]
    onTypeChange(next.key)
    radios.current.get(next.key)?.focus()
  }

  const placeholder = type === 'note' ? `Schrijf een notitie over ${relation.name}…` : 'Verslag (optioneel)…'

  return (
    <section
      ref={section}
      aria-label="Activiteit vastleggen"
      onKeyDown={onKeyDown}
      className="vonk-card flex flex-col gap-3 p-5"
    >
      {open ? (
        <>
          <div role="radiogroup" aria-label="Soort activiteit" className="flex flex-wrap gap-1.5">
            {TYPES.map((entry, index) => {
              const checked = entry.key === type
              return (
                <button
                  key={entry.key}
                  ref={(node) => {
                    if (node) radios.current.set(entry.key, node)
                    else radios.current.delete(entry.key)
                  }}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked ? 0 : -1}
                  disabled={saving}
                  onClick={() => onTypeChange(entry.key)}
                  onKeyDown={(event) => onRadioKeyDown(event, index)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm font-medium disabled:opacity-50 max-md:min-h-11',
                    checked
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  {entry.label}
                </button>
              )
            })}
          </div>

          {type !== 'note' ? (
            <div className="flex flex-wrap gap-2">
              <Input
                type="date"
                aria-label="Datum"
                max={todayIso()}
                value={draft.occurred_at}
                onChange={(event) => update({ occurred_at: event.target.value })}
                className="w-40 max-md:h-11 max-md:text-base"
              />
              <Input
                aria-label={type === 'email' ? 'Onderwerp' : 'Titel'}
                placeholder={type === 'email' ? 'Onderwerp (optioneel)' : 'Titel (optioneel)'}
                maxLength={200}
                value={draft.title}
                onChange={(event) => update({ title: event.target.value })}
                className="min-w-48 flex-1 max-md:h-11 max-md:text-base"
              />
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">Met</span>
            <span className="rounded-full bg-muted px-2.5 py-1 font-medium max-md:flex max-md:min-h-11 max-md:items-center">
              {relation.name}
            </span>
            {primary ? (
              draft.withOrganisation ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-muted py-1 pr-1 pl-2.5 font-medium max-md:min-h-11">
                  {primary.name}
                  <button
                    type="button"
                    aria-label={`${primary.name} weghalen`}
                    onClick={() => update({ withOrganisation: false })}
                    className="grid size-5 place-items-center rounded-full hover:bg-background max-md:size-9"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`${primary.name} toevoegen`}
                  onClick={() => update({ withOrganisation: true })}
                  className="rounded-full border border-dashed border-border px-2.5 py-1 text-muted-foreground hover:bg-muted hover:text-foreground max-md:min-h-11"
                >
                  + {primary.name}
                </button>
              )
            ) : null}
            {relation.kind === 'organisation' && members.length > 0 ? (
              <select
                aria-label="Contactpersoon"
                value={personId ?? ''}
                onChange={(event) => update({ personId: event.target.value || null })}
                className={selectClass}
              >
                <option value="">Geen contactpersoon</option>
                {members.map((member) => (
                  <option key={member.person_id} value={member.person_id}>
                    {member.full_name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>

          {/* TipTap reads the placeholder once, when the editor is created: a new editor per type
              gives a note "Schrijf een notitie…" and the other types "Verslag (optioneel)…". The
              draft of that type comes in through `value`. */}
          <RichTextEditor
            key={type}
            format="html"
            value={draft.body}
            onChange={(value) => update({ body: value })}
            ariaLabel="Tekst"
            placeholder={placeholder}
          />

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {`${hasContent(draft) ? 'Concept bewaard · ' : ''}⌘↵ opslaan`}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={cancel}>
                Annuleren
              </Button>
              <Button type="button" disabled={!canSave} onClick={() => void save()}>
                Opslaan
              </Button>
            </div>
          </div>
        </>
      ) : (
        <button
          ref={openButton}
          type="button"
          onClick={() => {
            onTypeChange('note')
            onOpenChange(true)
          }}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted max-md:min-h-11"
        >
          {`Schrijf een notitie over ${relation.name}…`}
        </button>
      )}

      {followUp && !open ? (
        <div role="group" aria-label="Vervolgtaak" className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Vervolgtaak?</span>
          {FOLLOW_UPS.map((pick) => (
            <Button
              key={pick.label}
              type="button"
              size="sm"
              variant="outline"
              disabled={followUpBusy}
              onClick={() => void planFollowUp(addDays(todayIso(), pick.days))}
            >
              {pick.label}
            </Button>
          ))}
          <Button type="button" size="sm" variant="outline" disabled={followUpBusy} onClick={() => setPickingDate(true)}>
            Datum…
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setFollowUp(null)}>
            Nee
          </Button>
          {pickingDate ? (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                aria-label="Datum vervolgtaak"
                min={todayIso()}
                value={followUpDate}
                onChange={(event) => setFollowUpDate(event.target.value)}
                className="w-40 max-md:h-11 max-md:text-base"
              />
              <Button
                type="button"
                size="sm"
                disabled={followUpBusy || followUpDate === ''}
                onClick={() => void planFollowUp(followUpDate)}
              >
                Aanmaken
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
